import {Component, Inject, OnInit} from '@angular/core';
import {MatDialog, MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {OrderItemToPackage} from '../../../../../model/OrderItemToPackage';
import {PrintNodeOrder} from '../../../../../model/PrintNodeOrder';
import {PrintNodeOrderService} from '../../../../service-data/print-node-order.service';
import {Package} from '../../../../../model/Package';
import {PrintNodeOrderItem} from '../../../../../model/PrintNodeOrderItem';
import {PackageType} from '../../../../../model/PackageType';
import {PackageService} from '../../../../service-data/package.service';
import {PackageTypeService} from '../../../../service-data/package-type.service';
import {PackageAndShippingComputationService} from '../../../../service-data/package-and-shipping-computation.service';
import {ShippingPackage} from '../../../../../model/cart/ShippingPackage';
import {ShippingOption} from '../../../../../model/cart/ShippingOption';
import {Address} from '../../../../../model/Address';
import {CARRIER_SERVICE_LEVELS} from '../../../../util/carrier-service-levels';
import {PackageOverrideDialogComponent} from './package-override-dialog.component';
import {AlertService} from '../../../../service-ui/alert.service';
import {LanguagePipe} from '../../../../pipe/language.pipe';

/** Per-package computed metrics */
interface PackageMetrics {
  totalWeight: number;
  bookWeight: number;
  totalBookVolume: number;
  packageVolume: number;
  volumePercent: number;
  weightPercent: number;
  stackedHeight: number;
  thicknessByTrimSize: { [trimSize: string]: number };
}

@Component({
  selector: 'app-change-packages',
  templateUrl: './change-packages.component.html',
  styleUrls: ['./change-packages.component.css']
})
export class ChangePackagesComponent implements OnInit {
  printNodeOrder: PrintNodeOrder;
  mobileQueryMatches: boolean;
  carrierServiceLevels = CARRIER_SERVICE_LEVELS;

  // Stage: 'edit' = propose changes, 'review' = confirm & commit
  stage: 'edit' | 'review' = 'edit';
  itemsPanelExpanded = false;

  // Working copies
  packagesArray: Package[] = [];
  packages: { [key: string]: Package } = {};
  orderItemToPackages: { [key: string]: OrderItemToPackage } = {};
  printNodeOrderItems: { [key: string]: PrintNodeOrderItem } = {};
  allItems: PrintNodeOrderItem[] = [];

  // Computed metrics per package
  packageMetrics: Map<Package, PackageMetrics> = new Map();

  // Shipping rates per package
  packageRates: Map<Package, ShippingOption[]> = new Map();
  ratesLoading: Map<Package, boolean> = new Map();

  // Change tracking
  addedPackages: Package[] = [];
  deletedPackages: Package[] = [];
  carrierServiceLevelChanges: any[] = [];
  packageTypeChanges: any[] = [];
  orderItemToPackageChanges: OrderItemToPackage[] = [];
  orderItemToPackageAdditions: OrderItemToPackage[] = [];

  // Original snapshots for dirty tracking
  originalQuantities: Map<string, number> = new Map();
  originalCarriers: Map<number, { carrier: string; serviceLevel: string }> = new Map();
  originalPackageTypes: Map<number, number> = new Map();

  /** Overrides the operator has approved during this dialog session
   *  via `PackageOverrideDialogComponent`. Keyed by `Package.package_ID`,
   *  value is the protection level the operator overrode
   *  (`'label'` = labels generated but not shipped;
   *  `'shipped'` = package already shipped). Once approved for a
   *  given package we don't re-prompt for additional changes to that
   *  same package. The map is also serialized into the
   *  `packageOverrides[]` field of the `PUT
   *  /printnodeorder/changepackages` payload so the server can
   *  honor the override and proceed even when its own guard would
   *  otherwise reject the change. See
   *  docs/contracts/shared/ChangePackages.md "Operator overrides". */
  approvedOverrides: Map<number, 'label' | 'shipped'> = new Map();

  nextNewPackageId = -1;

  pendingChanges: { type: string; description: string; pkg?: Package; item?: PrintNodeOrderItem }[] = [];

  saving = false;
  /** Error message surfaced inside the Review pane when the server
   *  rejects the change-packages PUT. Cleared when the user clicks
   *  Confirm again. */
  saveError = '';

  /** Non-fatal `warnings[]` returned by the server (see
   *  shared/ChangePackages.md). Surfaced in the Review pane after
   *  a successful save so the operator can still see them before
   *  closing the dialog. */
  saveWarnings: string[] = [];

  /** USPS refund result entries returned by the server.
   *  Phase 2 shape:
   *    {
   *      packageId, trackingNumber, refundRequested,
   *      refundReferenceId,           // carrier-side reference (or 'pending')
   *      supportNotified,
   *      provider,                    // e.g. 'SHIPENGINE'
   *      requestStatus,               // REQUESTED|SUBMITTED|APPROVED|DENIED|ERROR
   *      refundRequestId              // packagerefundrequest DB id
   *    }
   *  Rendered as a small result table in the Review pane after save. */
  saveRefundResults: any[] = [];

  /** True once a save has completed (with or without warnings). When
   *  the response is fully clean we auto-close the dialog; when there
   *  are warnings or refund results we keep the dialog open so the
   *  operator can read them and then click Close. */
  savedSummaryVisible = false;

  /** Structured conflict entries returned by an HTTP 409 from the
   *  server's protected-package guard. Each entry has the shape
   *  `{ packageId, requiredOverrideLevel, message }` (per the
   *  server contract). Rendered inline in red in the Review pane so
   *  the operator can see exactly which package needs which
   *  override level, then either go Back and re-approve or correct
   *  the change. See docs/contracts/shared/ChangePackages.md
   *  "Operator overrides". */
  conflictWarnings: Array<{ packageId?: number; requiredOverrideLevel?: string; message?: string }> = [];

  constructor(
    private printNodeOrderService: PrintNodeOrderService,
    private packageService: PackageService,
    public packageTypeService: PackageTypeService,
    private packageAndShippingComputationService: PackageAndShippingComputationService,
    public dialog: MatDialog,
    private alertService: AlertService,
    @Inject(MAT_DIALOG_DATA) data: any,
    private dialogRef: MatDialogRef<ChangePackagesComponent>
  ) {
    this.mobileQueryMatches = data.mobileQueryMatches;
    this.printNodeOrder = data.printNodeOrder;
    this.initializeWorkingData();
  }

  ngOnInit(): void {}

  private initializeWorkingData(): void {
    if (!this.printNodeOrder) { return; }
    this.allItems = this.printNodeOrder.printNodeOrderItems || [];

    if (this.printNodeOrder.packages) {
      for (const pkg of this.printNodeOrder.packages) {
        this.packagesArray.push(pkg);
        this.packages['' + pkg.package_ID] = pkg;
        this.originalCarriers.set(pkg.package_ID, { carrier: pkg.carrier, serviceLevel: pkg.servicelevel });
        this.originalPackageTypes.set(pkg.package_ID, pkg.packageType_ID);

        if (pkg.orderItemToPackages) {
          for (const oitp of pkg.orderItemToPackages) {
            if (oitp.orderItem) {
              for (const pnoi of oitp.orderItem.printNodeOrderItems) {
                const key = pnoi.ID + '-' + pkg.package_ID;
                this.orderItemToPackages[key] = oitp;
                this.printNodeOrderItems[key] = pnoi;
                this.originalQuantities.set(key, oitp.quantity);
              }
            }
          }
        }
        this.computePackageMetrics(pkg);
      }
    }

    for (const item of this.allItems) {
      this.recomputeItemQuantityInPackages(item);
    }
  }

  // ─── Volume / Weight Computations ───────────────────────────────

  getBookWidthInches(item: PrintNodeOrderItem): number {
    const book = item.book;
    const trimWidthInches = book.body.width / 72;
    if (book.bindingType === 'COIL') {
      return trimWidthInches + (book.body.spine / 72);
    }
    return trimWidthInches;
  }

  getBookHeightInches(item: PrintNodeOrderItem): number {
    return item.book.body.height / 72;
  }

  getBookSpineInches(item: PrintNodeOrderItem): number {
    return item.book.body.spine / 72;
  }

  getBookVolume(item: PrintNodeOrderItem): number {
    return this.getBookWidthInches(item) * this.getBookHeightInches(item) * this.getBookSpineInches(item);
  }

  computePackageMetrics(pkg: Package): void {
    let bookWeight = 0;
    let totalBookVolume = 0;
    let stackedHeight = 0;
    const thicknessByTrimSize: { [key: string]: number } = {};

    for (const item of this.allItems) {
      const key = item.ID + '-' + pkg.package_ID;
      const oitp = this.orderItemToPackages[key];
      if (!oitp || oitp.quantity <= 0) { continue; }

      const qty = oitp.quantity;
      bookWeight += qty * item.book.weight;
      totalBookVolume += qty * this.getBookVolume(item);

      const spineInches = this.getBookSpineInches(item);
      stackedHeight += qty * spineInches;

      const trimSize = (Math.round(item.book.body.width / 7.2) / 10) + 'x' + (Math.round(item.book.body.height / 7.2) / 10);
      thicknessByTrimSize[trimSize] = (thicknessByTrimSize[trimSize] || 0) + qty * spineInches;
    }

    const packageType = this.packageTypeService.getLocalItem(pkg.packageType_ID);
    const boxWeight = packageType ? packageType.boxWeight : 0;
    const maxWeight = packageType ? packageType.maxWeight : 70;
    const packageVolume = packageType ? (packageType.height * packageType.width * packageType.depth) : 0;
    const totalWeight = bookWeight + boxWeight;
    pkg.weight = totalWeight;

    this.packageMetrics.set(pkg, {
      totalWeight, bookWeight, totalBookVolume, packageVolume,
      volumePercent: packageVolume > 0 ? (totalBookVolume / packageVolume) * 100 : 0,
      weightPercent: maxWeight > 0 ? (totalWeight / maxWeight) * 100 : 0,
      stackedHeight, thicknessByTrimSize
    });
  }

  getMetrics(pkg: Package): PackageMetrics {
    return this.packageMetrics.get(pkg) || {
      totalWeight: 0, bookWeight: 0, totalBookVolume: 0,
      packageVolume: 0, volumePercent: 0, weightPercent: 0,
      stackedHeight: 0, thicknessByTrimSize: {}
    };
  }

  getThicknessTrimSizes(pkg: Package): string[] {
    return Object.keys(this.getMetrics(pkg).thicknessByTrimSize);
  }

  // ─── Item Quantity Management ───────────────────────────────────

  getQuantityInPackage(item: PrintNodeOrderItem, pkg: Package): number {
    const oitp = this.orderItemToPackages[item.ID + '-' + pkg.package_ID];
    return oitp ? oitp.quantity : 0;
  }

  getQuantityUnassigned(item: PrintNodeOrderItem): number {
    let assigned = 0;
    for (const pkg of this.packagesArray) {
      assigned += this.getQuantityInPackage(item, pkg);
    }
    return item.quantity - assigned;
  }

  recomputeItemQuantityInPackages(item: PrintNodeOrderItem): void {
    let total = 0;
    for (const pkg of this.packagesArray) {
      total += this.getQuantityInPackage(item, pkg);
    }
    item['quantityInPackages'] = total;
  }

  getPackageProtectionLevel(pkg: Package): 'none' | 'label' | 'shipped' {
    if (pkg.shippingStatus === 'SHIPPED' || this.printNodeOrder.shippingStatus === 'SHIPPED') {
      return 'shipped';
    }
    if (pkg.packageLabelStatus !== 'INITIAL' || pkg.shippingLabelStatus !== 'INITIAL') {
      return 'label';
    }
    return 'none';
  }

  async requireOverride(pkg: Package): Promise<boolean> {
    const level = this.getPackageProtectionLevel(pkg);
    if (level === 'none') { return true; }

    // Skip re-prompting if the operator has already approved an
    // override for this package in this dialog session. The approval
    // is also serialized into the save payload so the server honors
    // it on the back-end.
    if (this.approvedOverrides.has(pkg.package_ID)) { return true; }

    const dialogRef = this.dialog.open(PackageOverrideDialogComponent, {
      width: '450px',
      data: { level, pkg }
    });
    const approved = await dialogRef.afterClosed().toPromise().then(result => !!result);
    if (approved) {
      this.approvedOverrides.set(pkg.package_ID, level as 'label' | 'shipped');
    }
    return approved;
  }

  async changeQuantity(item: PrintNodeOrderItem, pkg: Package, delta: number): Promise<void> {
    if (delta === 0) { return; }
    const allowed = await this.requireOverride(pkg);
    if (!allowed) { return; }

    const key = item.ID + '-' + pkg.package_ID;
    let oitp = this.orderItemToPackages[key];

    if (!oitp) {
      oitp = new OrderItemToPackage(undefined);
      oitp.orderitem_ID = item.orderitem_ID;
      oitp.printnode_ID = pkg.printnode_ID;
      oitp.printnodeorderitem_ID = item.ID;
      oitp.package_ID = pkg.package_ID;
      oitp.quantity = 0;
      this.orderItemToPackages[key] = oitp;
      this.printNodeOrderItems[key] = item;
      if (!this.originalQuantities.has(key)) {
        this.originalQuantities.set(key, 0);
      }
      this.orderItemToPackageAdditions.push(oitp);
    }

    oitp.quantity = Math.max(0, oitp.quantity + delta);
    this.trackOrderItemToPackageChange(oitp);
    this.recomputeItemQuantityInPackages(item);
    this.computePackageMetrics(pkg);
    this.rebuildPendingChanges();
  }

  async moveAllToPackage(item: PrintNodeOrderItem, targetPkg: Package): Promise<void> {
    const allowed = await this.requireOverride(targetPkg);
    if (!allowed) { return; }

    for (const pkg of this.packagesArray) {
      if (pkg === targetPkg) { continue; }
      const key = item.ID + '-' + pkg.package_ID;
      const oitp = this.orderItemToPackages[key];
      if (oitp && oitp.quantity > 0) {
        const pkgAllowed = await this.requireOverride(pkg);
        if (!pkgAllowed) { return; }
        oitp.quantity = 0;
        this.trackOrderItemToPackageChange(oitp);
        this.computePackageMetrics(pkg);
      }
    }

    const targetKey = item.ID + '-' + targetPkg.package_ID;
    let targetOitp = this.orderItemToPackages[targetKey];
    if (!targetOitp) {
      targetOitp = new OrderItemToPackage(undefined);
      targetOitp.orderitem_ID = item.orderitem_ID;
      targetOitp.printnode_ID = targetPkg.printnode_ID;
      targetOitp.printnodeorderitem_ID = item.ID;
      targetOitp.package_ID = targetPkg.package_ID;
      targetOitp.quantity = 0;
      this.orderItemToPackages[targetKey] = targetOitp;
      this.printNodeOrderItems[targetKey] = item;
      if (!this.originalQuantities.has(targetKey)) {
        this.originalQuantities.set(targetKey, 0);
      }
      this.orderItemToPackageAdditions.push(targetOitp);
    }
    targetOitp.quantity = item.quantity;
    this.trackOrderItemToPackageChange(targetOitp);
    this.recomputeItemQuantityInPackages(item);
    this.computePackageMetrics(targetPkg);
    this.rebuildPendingChanges();
  }

  private trackOrderItemToPackageChange(oitp: OrderItemToPackage): void {
    const idx = this.orderItemToPackageChanges.findIndex(c => c.getId() === oitp.getId());
    const key = this.findKeyForOitp(oitp);
    const originalQty = this.originalQuantities.get(key) || 0;
    if (oitp.quantity === originalQty) {
      if (idx !== -1) { this.orderItemToPackageChanges.splice(idx, 1); }
    } else {
      if (idx === -1) { this.orderItemToPackageChanges.push(oitp); }
    }
  }

  private findKeyForOitp(oitp: OrderItemToPackage): string {
    for (const key of Object.keys(this.orderItemToPackages)) {
      if (this.orderItemToPackages[key] === oitp) { return key; }
    }
    return '';
  }

  // ─── Package Management ─────────────────────────────────────────

  addPackage(): void {
    const pkg = new Package(undefined);
    pkg.printNodeOrder = this.printNodeOrder;
    pkg.printNodeOrder_ID = this.printNodeOrder.ID;
    pkg.printnode_ID = this.printNodeOrder.printnode_ID;
    pkg.carrier = this.printNodeOrder.carrier;
    pkg.servicelevel = this.printNodeOrder.servicelevel;
    pkg.weight = 0;
    pkg.package_ID = this.nextNewPackageId--;
    pkg.shippingStatus = 'NONE';
    pkg.packageLabelStatus = 'INITIAL';
    pkg.shippingLabelStatus = 'INITIAL';
    pkg.orderItemToPackages = [];

    if (this.packagesArray.length > 0) {
      pkg.packageType_ID = this.packagesArray[0].packageType_ID;
    } else {
      pkg.packageType_ID = 5;
    }

    this.packagesArray.push(pkg);
    this.packages['' + pkg.package_ID] = pkg;
    this.addedPackages.push(pkg);
    this.computePackageMetrics(pkg);
    this.rebuildPendingChanges();
  }

  canDeletePackage(pkg: Package): boolean {
    // Delete is always offered — the {@link deletePackage} handler
    // drains any item assignments back to "unassigned" before
    // removing the package (2026-07-20 UX ask: operators dealing
    // with orders that have many redundant / mis-packed packages
    // shouldn't have to click through every item to zero it out
    // manually before delete becomes available). The
    // {@link requireOverride} guard still fires for labeled /
    // shipped packages so an accidental delete can't proceed
    // without operator confirmation.
    return !!pkg;
  }

  async deletePackage(pkg: Package): Promise<void> {
    if (!pkg) { return; }
    const allowed = await this.requireOverride(pkg);
    if (!allowed) { return; }

    // Drain every item currently assigned to this package back to
    // zero so the affected items become "unassigned" once the
    // package is gone. Uses `setQuantityInPackage`-equivalent
    // bookkeeping directly (bypassing the per-call override
    // prompt — we already approved above and re-prompting per item
    // would be a horrible UX).
    for (const item of this.allItems) {
      const key = item.ID + '-' + pkg.package_ID;
      const oitp = this.orderItemToPackages[key];
      if (oitp && oitp.quantity > 0) {
        oitp.quantity = 0;
        this.trackOrderItemToPackageChange(oitp);
        this.recomputeItemQuantityInPackages(item);
      }
    }
    this.computePackageMetrics(pkg);

    const idx = this.packagesArray.indexOf(pkg);
    if (idx !== -1) { this.packagesArray.splice(idx, 1); }
    delete this.packages['' + pkg.package_ID];

    const addedIdx = this.addedPackages.indexOf(pkg);
    if (addedIdx !== -1) {
      this.addedPackages.splice(addedIdx, 1);
    } else {
      this.deletedPackages.push(pkg);
    }
    this.packageMetrics.delete(pkg);
    this.packageRates.delete(pkg);
    this.rebuildPendingChanges();
  }

  async changePackageCarrierAndServiceLevelCombined(pkg: Package, carrierServiceLevel: string): Promise<void> {
    const index = carrierServiceLevel.indexOf('_');
    if (index <= 0) { return; }
    const carrier = carrierServiceLevel.substring(0, index);
    const serviceLevel = carrierServiceLevel.substring(index + 1);

    const allowed = await this.requireOverride(pkg);
    if (!allowed) { return; }

    this.updateCarrierServiceLevelChange(pkg, carrier, serviceLevel);
    pkg.carrier = carrier;
    pkg.servicelevel = serviceLevel;
    this.packageRates.delete(pkg);
    this.rebuildPendingChanges();
  }

  private updateCarrierServiceLevelChange(pkg: Package, carrier: string, serviceLevel: string): void {
    const idx = this.carrierServiceLevelChanges.findIndex(c => c.packageId === pkg.package_ID);
    if (idx !== -1) { this.carrierServiceLevelChanges.splice(idx, 1); }
    const original = this.originalCarriers.get(pkg.package_ID);
    if (original && (carrier !== original.carrier || serviceLevel !== original.serviceLevel)) {
      this.carrierServiceLevelChanges.push({
        packageId: pkg.package_ID, carrier, serviceLevel,
        originalCarrier: original.carrier, originalServiceLevel: original.serviceLevel
      });
    }
  }

  async changePackageType(pkg: Package, packageTypeId: number): Promise<void> {
    const allowed = await this.requireOverride(pkg);
    if (!allowed) { return; }

    const idx = this.packageTypeChanges.findIndex(c => c.packageId === pkg.package_ID);
    if (idx !== -1) { this.packageTypeChanges.splice(idx, 1); }

    const originalTypeId = this.originalPackageTypes.get(pkg.package_ID);
    if (packageTypeId !== originalTypeId) {
      this.packageTypeChanges.push({
        packageId: pkg.package_ID, packageType: packageTypeId, originalPackageType: originalTypeId
      });
    }
    pkg.packageType_ID = packageTypeId;
    this.computePackageMetrics(pkg);
    this.packageRates.delete(pkg);
    this.rebuildPendingChanges();
  }

  // ─── Rate Fetching ──────────────────────────────────────────────

  getShippingRates(pkg: Package): void {
    const packageType = this.packageTypeService.getLocalItem(pkg.packageType_ID);
    if (!packageType) { return; }

    const metrics = this.getMetrics(pkg);
    // Pass `packageTypeId` (positional arg 9) so the server's rating call
    // can look up the concrete printnode_packagetype instead of guessing
    // from dimensions. Constructor signature:
    //   (height, width, depth, weight, quantity, shippingOptions,
    //    itemsPerPackage?, packingCost?, packageTypeId?, packageIndex?)
    const shippingPackage = new ShippingPackage(
      packageType.height, packageType.width, packageType.depth,
      metrics.totalWeight, 1, [],
      undefined, undefined, pkg.packageType_ID
    );

    this.ratesLoading.set(pkg, true);
    this.packageRates.delete(pkg);

    this.packageAndShippingComputationService.getShippingOptions(
      pkg.carrier, pkg.servicelevel,
      this.printNodeOrder.printnode_ID,
      this.buildAddress(),
      shippingPackage, 1
    ).subscribe(
      (result: any) => {
        this.ratesLoading.set(pkg, false);
        if (result && result.shippingOptions) {
          this.packageRates.set(pkg, result.shippingOptions.map(
            (o: any) => new ShippingOption(
              o.carrier, o.serviceLevel, o.cost, o.deliveryDays,
              undefined, undefined, o.deliveryDaysMin, o.deliveryDaysMax
            )
          ));
        } else if (result && Array.isArray(result)) {
          this.packageRates.set(pkg, result.map(
            (o: any) => new ShippingOption(
              o.carrier, o.serviceLevel, o.cost, o.deliveryDays,
              undefined, undefined, o.deliveryDaysMin, o.deliveryDaysMax
            )
          ));
        }
      },
      () => { this.ratesLoading.set(pkg, false); }
    );
  }

  getAllShippingRates(): void {
    for (const pkg of this.packagesArray) {
      this.getShippingRates(pkg);
    }
  }

  private buildAddress(): Address {
    const addr = new Address(undefined);
    addr.address1 = this.printNodeOrder.address1;
    addr.address2 = this.printNodeOrder.address2;
    addr.city = this.printNodeOrder.city;
    addr.state = this.printNodeOrder.state;
    addr.country = this.printNodeOrder.country;
    addr.postalcode = this.printNodeOrder.postalcode;
    return addr;
  }

  getRates(pkg: Package): ShippingOption[] {
    return this.packageRates.get(pkg) || [];
  }

  isRatesLoading(pkg: Package): boolean {
    return this.ratesLoading.get(pkg) || false;
  }

  // ─── Pending Changes ───────────────────────────────────────────

  rebuildPendingChanges(): void {
    this.pendingChanges = [];
    for (const pkg of this.addedPackages) {
      this.pendingChanges.push({ type: 'add_package', description: 'Add new package', pkg });
    }
    for (const pkg of this.deletedPackages) {
      this.pendingChanges.push({ type: 'delete_package', description: `Delete package #${pkg.package_ID}`, pkg });
      // A deleted package that already has a USPS shipping label
      // needs a postage refund request — the label is now unused
      // and the operator would otherwise eat the cost. Mirrors the
      // carrier-change refund below.
      if (pkg.carrier === 'USPS' && pkg.shippingLabelStatus !== 'INITIAL') {
        this.pendingChanges.push({ type: 'usps_refund', description: `Request USPS postage refund for deleted pkg #${pkg.package_ID}`, pkg });
      }
    }
    for (const change of this.carrierServiceLevelChanges) {
      this.pendingChanges.push({
        type: 'change_carrier',
        description: `Pkg #${change.packageId}: ${change.originalCarrier} ${change.originalServiceLevel} → ${change.carrier} ${change.serviceLevel}`
      });
      if (change.originalCarrier === 'USPS' && change.carrier !== 'USPS') {
        const pkg = this.packages['' + change.packageId];
        if (pkg && pkg.shippingLabelStatus !== 'INITIAL') {
          this.pendingChanges.push({ type: 'usps_refund', description: `Request USPS postage refund for pkg #${change.packageId}`, pkg });
        }
      }
    }
    for (const change of this.packageTypeChanges) {
      const newType = this.packageTypeService.getLocalItem(change.packageType);
      const oldType = this.packageTypeService.getLocalItem(change.originalPackageType);
      this.pendingChanges.push({
        type: 'change_package_type',
        description: `Pkg #${change.packageId}: ${oldType?.name || change.originalPackageType} → ${newType?.name || change.packageType}`
      });
    }
    for (const oitp of this.orderItemToPackageChanges) {
      const key = this.findKeyForOitp(oitp);
      const item = this.printNodeOrderItems[key];
      const origQty = this.originalQuantities.get(key) || 0;
      if (item) {
        this.pendingChanges.push({
          type: 'move',
          description: `"${item.book?.title || item.ID}" in pkg #${oitp.package_ID}: ${origQty} → ${oitp.quantity}`,
          item
        });
      }
    }
  }

  get hasPendingChanges(): boolean {
    return this.addedPackages.length > 0 || this.deletedPackages.length > 0 ||
      this.carrierServiceLevelChanges.length > 0 || this.packageTypeChanges.length > 0 ||
      this.orderItemToPackageChanges.length > 0;
  }

  get hasQuantityMismatch(): boolean {
    for (const item of this.allItems) {
      if (this.getQuantityUnassigned(item) !== 0) { return true; }
    }
    return false;
  }

  goToReview(): void {
    this.rebuildPendingChanges();
    this.stage = 'review';
  }

  goBackToEdit(): void {
    this.stage = 'edit';
  }

  saveChanges(): void {
    this.saving = true;
    this.saveError = '';
    this.saveWarnings = [];
    this.saveRefundResults = [];
    this.conflictWarnings = [];
    this.savedSummaryVisible = false;
    const uspsRefundPackageIds: number[] = [];
    for (const change of this.carrierServiceLevelChanges) {
      if (change.originalCarrier === 'USPS' && change.carrier !== 'USPS') {
        const pkg = this.packages['' + change.packageId];
        if (pkg && pkg.shippingLabelStatus !== 'INITIAL') {
          uspsRefundPackageIds.push(change.packageId);
        }
      }
    }
    // Deleted USPS packages that already had a label need a refund
    // too — the label was purchased but will never be used. See
    // {@link rebuildPendingChanges} where this mirrors the pending
    // `usps_refund` entry the operator sees on the review pane.
    for (const pkg of this.deletedPackages) {
      if (pkg.carrier === 'USPS' && pkg.shippingLabelStatus !== 'INITIAL') {
        if (uspsRefundPackageIds.indexOf(pkg.package_ID) === -1) {
          uspsRefundPackageIds.push(pkg.package_ID);
        }
      }
    }

    // Build the additions set first so we can exclude those rows
    // from the changes array. An OITP for a brand-new (or
    // previously-absent) link is tracked in BOTH
    // `orderItemToPackageAdditions` (because it didn't exist before)
    // AND `orderItemToPackageChanges` (because
    // `trackOrderItemToPackageChange` records any non-zero delta from
    // the original quantity of 0). Sending the same row in both
    // arrays causes the server to attempt an insert and then an
    // update against the same composite PK and produces the
    // "Duplicate entry … for key 'orderitemtopackage.PRIMARY'" error.
    // Additions also drop any row that ended up back at quantity 0
    // (i.e. created and then drained again before save) — there's no
    // row to insert.
    const additionSet = new Set(this.orderItemToPackageAdditions);
    const additionsPayload = this.orderItemToPackageAdditions
      .filter(o => (o.quantity || 0) > 0)
      .map(o => ({
        orderitem_ID: o.orderitem_ID,
        printnode_ID: o.printnode_ID,
        printnodeorderitem_ID: o.printnodeorderitem_ID,
        package_ID: o.package_ID,
        quantity: o.quantity
      }));
    const changesPayload = this.orderItemToPackageChanges
      .filter(o => !additionSet.has(o))
      .map(o => ({
        orderitem_ID: o.orderitem_ID,
        printnode_ID: o.printnode_ID,
        printnodeorderitem_ID: o.printnodeorderitem_ID,
        package_ID: o.package_ID,
        quantity: o.quantity
      }));

    const packageChanges = {
      printNodeOrder_ID: this.printNodeOrder.ID,
      addedPackages: this.addedPackages.map(p => ({
        carrier: p.carrier, servicelevel: p.servicelevel,
        packageType_ID: p.packageType_ID, printnode_ID: p.printnode_ID,
        printNodeOrder_ID: p.printNodeOrder_ID
      })),
      deletedPackages: this.deletedPackages.map(p => ({ packageId: p.package_ID })),
      carrierServiceLevelChanges: this.carrierServiceLevelChanges,
      packageTypeChanges: this.packageTypeChanges,
      // Strip live linked-object back-references (`package`, `orderItem`,
      // `printNodeOrderItem`) before sending. These are populated by the
      // DataService link mechanism and create circular structures
      // (OrderItemToPackage.package -> Package.orderItemToPackages[]),
      // which crashes Angular's HttpClient JSON.stringify with
      // "Converting circular structure to JSON". Send only the scalar
      // fields the server needs.
      orderItemToPackageChanges: changesPayload,
      orderItemToPackageAdditions: additionsPayload,
      uspsRefundPackageIds,

      // Operator-approved overrides for packages whose label was
      // already generated (`label`) or that have already shipped
      // (`shipped`). The server's normal label-staleness /
      // shipped-package guard should accept the mutation when the
      // package's ID appears here. See
      // docs/contracts/shared/ChangePackages.md "Operator overrides".
      packageOverrides: Array.from(this.approvedOverrides.entries()).map(
        ([packageId, overrideLevel]) => ({ packageId, overrideLevel })
      )
    };

    // Wrap the service call in a try/catch so that synchronous
    // failures (e.g. JSON serialization errors thrown by HttpClient
    // when the payload contains a cycle, or a thrown
    // `getHttpOptions()` precondition) are surfaced inline in the
    // dialog instead of bubbling to `GlobalErrorHandler` / the global
    // alert/snackbar. All error reporting for this transaction
    // happens inline in the Review pane — we no longer call
    // `alertService.error(...)` because the user wants the error
    // shown next to the Back/Cancel buttons that originated the
    // transaction, not in the global toast system.
    let saveStream;
    try {
      saveStream = this.printNodeOrderService.changePackages(packageChanges);
    } catch (syncErr: any) {
      this.saving = false;
      this.handleSaveError(syncErr);
      return;
    }

    saveStream.subscribe(
      (response: any) => {
        this.saving = false;
        if (response === undefined || response === null || response === false) {
          this.saveError = LanguagePipe.get(
            'Could not save package changes. Please try again.',
            'Could not save package changes. Please try again.'
          );
          return;
        }

        // Capture optional warnings[] and uspsRefunds[] for in-pane
        // display. See docs/contracts/shared/ChangePackages.md.
        this.saveWarnings = Array.isArray(response.warnings) ? response.warnings : [];
        this.saveRefundResults = Array.isArray(response.uspsRefunds) ? response.uspsRefunds : [];

        // If there's nothing extra to read, close immediately. If
        // there are warnings or refund results, keep the dialog open
        // and let the user click Close after reviewing. Success
        // confirmation is implicit (dialog closes) when clean — no
        // global success toast (per request: keep all transaction
        // feedback inside the originating dialog).
        if (this.saveWarnings.length === 0 && this.saveRefundResults.length === 0) {
          this.dialogRef.close(true);
        } else {
          this.savedSummaryVisible = true;
        }
      },
      (err: any) => {
        this.saving = false;
        this.handleSaveError(err);
      }
    );
  }

  /**
   * Inline error handler for the Change Packages save flow.
   *
   * All save-side errors — whether thrown synchronously by HttpClient
   * (e.g. circular-JSON serialization), surfaced asynchronously as an
   * `HttpErrorResponse` (the service intentionally bypasses
   * `handleErrorX` for `changePackages`), or any other unexpected
   * shape — are rendered inline in the Review pane next to the Back
   * and Cancel buttons. We intentionally do NOT call
   * `AlertService.error(...)` here: the operator should see the
   * failure in the same dialog that originated the transaction.
   *
   * Distinguishes HTTP 409 (protected-package guard rejection with
   * structured `{ packageId, requiredOverrideLevel, message }`
   * entries — see docs/contracts/shared/ChangePackages.md
   * "Operator overrides") from generic 500/network/synchronous
   * failures.
   */
  private handleSaveError(err: any): void {
    const status = err && err.status;
    const body = err && err.error;
    const serverWarnings: any[] = body && Array.isArray(body.warnings)
      ? body.warnings
      : [];

    if (status === 409) {
      this.conflictWarnings = serverWarnings.length > 0
        ? serverWarnings
        : [{ message: LanguagePipe.get(
              'The change was rejected because of a conflict, but the server did not include details.',
              'The change was rejected because of a conflict, but the server did not include details.'
            ) }];
      this.saveError = (body && body.error)
        ? body.error
        : LanguagePipe.get(
            'The change was rejected because one or more packages are protected. Approve the override(s) and try again.',
            'The change was rejected because one or more packages are protected. Approve the override(s) and try again.'
          );
      return;
    }

    // 500 / network / synchronous / other → generic inline error,
    // plus any server-supplied string warnings.
    this.saveWarnings = serverWarnings.filter(w => typeof w === 'string');
    this.saveError = (body && body.error)
      || (err && err.message)
      || (typeof err === 'string' ? err : '')
      || LanguagePipe.get(
           'Could not save package changes. Please try again.',
           'Could not save package changes. Please try again.'
         );
    // Log to console for diagnostics but DO NOT surface to the global
    // alert/snackbar — the inline `saveError` is the user-facing
    // surface for this transaction.
    console.error('ChangePackages save failed', err);
  }

  /** Close the dialog after the user has read the post-save summary. */
  closeAfterSummary(): void {
    this.dialogRef.close(true);
  }

  /** Human-readable label for a single 409 conflict entry. */
  getConflictText(c: { packageId?: number; requiredOverrideLevel?: string; message?: string }): string {
    if (!c) { return ''; }
    if (c.message) { return c.message; }
    const id = (c.packageId !== undefined) ? '#' + c.packageId : '';
    const level = c.requiredOverrideLevel || '';
    return [LanguagePipe.get('Package', 'Package'), id, level && `(${level} override required)`]
      .filter(Boolean)
      .join(' ');
  }

  /** Human-friendly label for a refund row's requestStatus. */
  getRefundStatusLabel(status: string): string {
    switch (status) {
      case 'REQUESTED': return LanguagePipe.get('Requested', 'Requested');
      case 'SUBMITTED': return LanguagePipe.get('Submitted', 'Submitted');
      case 'APPROVED':  return LanguagePipe.get('Approved', 'Approved');
      case 'DENIED':    return LanguagePipe.get('Denied', 'Denied');
      case 'ERROR':     return LanguagePipe.get('Error', 'Error');
      default:          return status || '';
    }
  }

  /** CSS modifier for a refund row based on its requestStatus. */
  getRefundStatusClass(status: string): string {
    switch (status) {
      case 'APPROVED':  return 'cp-refund-ok';
      case 'SUBMITTED':
      case 'REQUESTED': return 'cp-refund-pending';
      case 'DENIED':
      case 'ERROR':     return 'cp-refund-error';
      default:          return '';
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  getCapacityBarClass(percent: number): string {
    if (percent > 100) { return 'capacity-over'; }
    if (percent > 85) { return 'capacity-warn'; }
    return 'capacity-ok';
  }

  getPackageTypeForPkg(pkg: Package): PackageType | undefined {
    return this.packageTypeService.getLocalItem(pkg.packageType_ID);
  }

  getFilteredPackageTypes(pkg: Package): PackageType[] {
    if (!this.packageTypeService.objects) { return []; }
    return this.packageTypeService.objects.filter(pt =>
      (pt.carrier === 'ANY' && pkg.servicelevel !== 'PRIORITY_FLAT' && pkg.servicelevel !== 'EXPRESS') ||
      (pt.carrier === pkg.carrier && pt.service === pkg.servicelevel)
    );
  }

  getProtectionIcon(pkg: Package): string {
    const level = this.getPackageProtectionLevel(pkg);
    if (level === 'shipped') { return 'local_shipping'; }
    if (level === 'label') { return 'label'; }
    return '';
  }

  getProtectionTooltip(pkg: Package): string {
    const level = this.getPackageProtectionLevel(pkg);
    if (level === 'shipped') { return 'This package has been shipped. Changes require override.'; }
    if (level === 'label') { return 'Labels have been generated. Changes require override.'; }
    return '';
  }

  /**
   * Set exact quantity from typed input. Allows temporary over-assignment.
   */
  async setQuantityInPackage(item: PrintNodeOrderItem, pkg: Package, value: string): Promise<void> {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 0) { return; }

    const currentQty = this.getQuantityInPackage(item, pkg);
    const delta = parsed - currentQty;
    if (delta === 0) { return; }

    const allowed = await this.requireOverride(pkg);
    if (!allowed) { return; }

    const key = item.ID + '-' + pkg.package_ID;
    let oitp = this.orderItemToPackages[key];

    if (!oitp) {
      oitp = new OrderItemToPackage(undefined);
      oitp.orderitem_ID = item.orderitem_ID;
      oitp.printnode_ID = pkg.printnode_ID;
      oitp.printnodeorderitem_ID = item.ID;
      oitp.package_ID = pkg.package_ID;
      oitp.quantity = 0;
      this.orderItemToPackages[key] = oitp;
      this.printNodeOrderItems[key] = item;
      if (!this.originalQuantities.has(key)) {
        this.originalQuantities.set(key, 0);
      }
      this.orderItemToPackageAdditions.push(oitp);
    }

    oitp.quantity = parsed;
    this.trackOrderItemToPackageChange(oitp);
    this.recomputeItemQuantityInPackages(item);
    this.computePackageMetrics(pkg);
    this.rebuildPendingChanges();
  }

  /**
   * Transfer 1 unit of an item from one package to another.
   */
  async transferOneToPackage(item: PrintNodeOrderItem, fromPkg: Package, toPkg: Package): Promise<void> {
    if (this.getQuantityInPackage(item, fromPkg) <= 0) { return; }

    const fromAllowed = await this.requireOverride(fromPkg);
    if (!fromAllowed) { return; }
    const toAllowed = await this.requireOverride(toPkg);
    if (!toAllowed) { return; }

    await this.changeQuantity(item, fromPkg, -1);
    await this.changeQuantity(item, toPkg, 1);
  }

  /**
   * Get all other packages (for transfer buttons).
   */
  getTransferTargetPackages(currentPkg: Package): Package[] {
    return this.packagesArray.filter(p => p !== currentPkg);
  }
}
